import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";

import { Case, CaseDocumentWithPresignedUrl, Slide } from "pwapi";

import { ViewerMode } from "custom-models/ViewerTypes";

import { fetchCaseDiagnoses } from "shared/actions/caseDetailActions";
import {
  useCanFlagSlides,
  useCanSeeLabStatus,
} from "shared/hooks/usePermissions";
import { AuthState } from "shared/reducers/authReducer";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { FeatureFlags } from "shared/reducers/sysReducer";

import { ReactComponent as CountIcon } from "icons/count.svg";
import { ReactComponent as GrabIcon } from "icons/grab.svg";
import { ReactComponent as PencilIcon } from "icons/pencil.svg";
import { ReactComponent as PolygonIcon } from "icons/polygon.svg";
import { ReactComponent as RulerIcon } from "icons/ruler.svg";
import { ReactComponent as SidebarIcon } from "icons/sidebar.svg";
import { ReactComponent as TextIcon } from "icons/text.svg";
import ApplicationState from "store/ApplicationState";

import AIButton from "./AIButton";
import AnnotationToolButton from "./AnnotationToolButton";
import DiagnosisDialogButton from "./dialogs/DiagnosisDialogButton";
import { DialogEnum } from "./dialogs/Dialog";
import DialogButton from "./dialogs/DialogButton";
import { slideClassification } from "./dialogs/utils";
import FlaggingTool from "./FlaggingTool";

type SlideButtonsProps = {
  caseDetailObject: CaseDetailState;
  slideDetailObject?: SlideDetailState;
  authObject: AuthState;
  dialog: DialogEnum | null;
  setDialog: Function;
  archived: Boolean;
  sideWindowOpen: Boolean;
  setSideWindowOpen: Function;
  document?: CaseDocumentWithPresignedUrl;
  specimenId?: number;
};

const SlideButtons = ({
  caseDetailObject,
  slideDetailObject,
  authObject,
  dialog,
  setDialog,
  archived,
  sideWindowOpen,
  setSideWindowOpen,
  document,
  specimenId,
}: SlideButtonsProps) => {
  const dispatch = useDispatch();
  const canFlag = useCanFlagSlides();
  const canSeeLabStatus = useCanSeeLabStatus();

  const classificationInfo =
    slideDetailObject && slideClassification(slideDetailObject);

  const features: FeatureFlags = useSelector(
    ({ sys }: ApplicationState) => sys.features
  );

  // NOTE: We need to dig into the API to get "LIS-SENT" diagnosis,
  // ... in order to display them in a context-rich way (because the
  // truly "LIS-FINALIZED" diagnosis is "flat" and missing codes)
  useEffect(() => {
    if (caseDetailObject.status === Case.status.FINALIZED)
      caseDetailObject.id &&
        dispatch(
          fetchCaseDiagnoses(caseDetailObject.id, caseDetailObject.status)
        );
  }, [caseDetailObject.id, caseDetailObject.status]);

  const specimen = (caseDetailObject.specimens || []).find((specimen) =>
    [
      slideDetailObject?.specimen?.id,
      document?.specimen_id,
      specimenId,
    ].includes(specimen.id)
  );

  return (
    <div className="SlideButtons">
      <nav className="SlideButtons__dialogs">
        {specimen && (
          <DialogButton
            currentDialog={dialog}
            wantedDialog={DialogEnum.Specimen}
            setDialog={setDialog}
            hotkey="s, shift+s"
            title="Specimen"
          >
            <u>S</u>pecimen&nbsp;{specimen.name}
            {caseDetailObject.accession_prefix?.is_diagnostic &&
              (!specimen.gross_description || !specimen.clinical_history) && (
                <FontAwesomeIcon icon="exclamation-circle" />
              )}
          </DialogButton>
        )}
        {!features?.technicalReportsOnly && (
          <DiagnosisDialogButton
            caseDetailObject={caseDetailObject}
            dialog={dialog}
            setDialog={setDialog}
          />
        )}
        {!features?.mrmcStudy &&
          features.aiOverlay &&
          classificationInfo !== undefined && (
            <AIButton currentDialog={dialog} setDialog={setDialog} />
          )}
        <DialogButton
          currentDialog={dialog}
          wantedDialog={DialogEnum.StainOrdering}
          setDialog={setDialog}
          hotkey="o, shift+o"
          title="Stain Ordering"
        >
          <u>O</u>rders
        </DialogButton>
      </nav>

      {!features?.mrmcStudy && (
        <nav className="SlideButtons__tools">
          {!archived && !!slideDetailObject?.id && (
            <>
              {slideDetailObject.status === Slide.status.SUCCESS && (
                <>
                  <AnnotationToolButton
                    slideDetailObject={slideDetailObject}
                    wantedViewerMode={ViewerMode.PAN_ZOOM}
                    authObject={authObject}
                    title="Pan/Zoom"
                  >
                    <GrabIcon className="svg-icon__large" />
                  </AnnotationToolButton>

                  <AnnotationToolButton
                    slideDetailObject={slideDetailObject}
                    wantedViewerMode={ViewerMode.MEASUREMENT}
                    authObject={authObject}
                    title="Measure"
                    mppRequired={true}
                  >
                    <RulerIcon className="svg-icon__large" />
                  </AnnotationToolButton>

                  <AnnotationToolButton
                    slideDetailObject={slideDetailObject}
                    wantedViewerMode={ViewerMode.PEN_DRAWING}
                    authObject={authObject}
                    title="Draw"
                  >
                    <PencilIcon className="svg-icon__large" />
                  </AnnotationToolButton>

                  <AnnotationToolButton
                    slideDetailObject={slideDetailObject}
                    wantedViewerMode={ViewerMode.TEXT}
                    authObject={authObject}
                    title="Text"
                  >
                    <TextIcon className="svg-icon__large" />
                  </AnnotationToolButton>
                  <AnnotationToolButton
                    slideDetailObject={slideDetailObject}
                    wantedViewerMode={ViewerMode.AREA_COUNT}
                    authObject={authObject}
                    title="Area Count"
                    mppRequired={true}
                  >
                    <CountIcon className="svg-icon__large" />
                  </AnnotationToolButton>
                  {features?.prospectiveAI &&
                    classificationInfo !== undefined && (
                      <AnnotationToolButton
                        wantedViewerMode={ViewerMode.ROI}
                        slideDetailObject={slideDetailObject}
                        authObject={authObject}
                        title="Region of Interest"
                        mppRequired={true}
                        maskRequired={true}
                      >
                        <PolygonIcon className="svg-icon__large" />
                      </AnnotationToolButton>
                    )}
                </>
              )}
              {canFlag && (
                <FlaggingTool
                  caseDetail={caseDetailObject}
                  slideDetail={slideDetailObject}
                />
              )}
            </>
          )}
          <button
            title="Annotations"
            onClick={() => {
              setSideWindowOpen(!sideWindowOpen);
            }}
            data-tip=""
            data-for="slide-side-window"
            className="side-window-opener"
          >
            <SidebarIcon
              className="svg-icon__large"
              style={{
                marginBottom: "1px",
              }}
            />
          </button>
          <ReactTooltip place="left" id="slide-side-window" type="info">
            Case Log
            {!!slideDetailObject?.id ? " / Slide Annotations" : ""}
            {canSeeLabStatus && caseDetailObject?.source === Case.source.PW
              ? " / Lab Status"
              : ""}
          </ReactTooltip>
        </nav>
      )}
    </div>
  );
};

export default SlideButtons;
